export type DataIssue = {
  month: string;
  kind: "partial" | "stale";
  /** A module-worded sentence that replaces the generated one (a notice that is not about a month). */
  message?: string;
};

function issueText(issue: DataIssue): string {
  if (issue.message) return issue.message;
  // "partial": `month` is the bucket still accumulating (this month).
  // "stale": `month` is the freshness bound - a month that HAS closed but
  // is missing from the series, i.e. the producer has not backfilled it -
  // the opposite claim from "still open", so the two must never share a
  // sentence.
  return issue.kind === "partial"
    ? `${issue.month} is still open`
    : `${issue.month} has closed but is missing from this series`;
}

/**
 * A range's data-honesty issues, announced above its charts (W10, parity
 * polish). `role="status"` so a screen reader announces a LATER change to
 * this content. That only works if the live region already exists in the
 * DOM before the content changes - most screen readers do not announce a
 * region that appears for the first time already populated - so the
 * element is always mounted; it is empty (and visually collapsed) rather
 * than absent when there is nothing to say.
 */
export function DataIssueBanner({ issues }: { issues: DataIssue[] }) {
  if (issues.length === 0) {
    return <p role="status" className="sr-only" />;
  }

  const text = issues.map(issueText).join("; ");

  return (
    <p
      role="status"
      className="rounded-ks-sm border border-ks-warn/30 bg-ks-warn/10 px-3 py-2 text-xs text-ks-ink"
    >
      {text}
    </p>
  );
}
